import { Injectable, NgZone } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthenticationService } from './auth.service';
import { ProductoService } from '../../shared/services/productos.service';

export interface MonitoreoConnectedPayload {
  message: string;
  userId?: string;
  cliente?: string;
  rooms?: string[];
}

export interface FindProductsByIdsPayload {
  idProductos?: number[];
  productos?: any[];
  data?: any[];
}

/** Payload de `entrada-producto-stock` y `salida-producto-stock`. */
export interface ProductoStockSocketPayload {
  idProducto: number;
  idSucursal: number;
  nuevoStock: number;
  idSalida: number;
}

@Injectable({ providedIn: 'root' })
export class MonitoreoSocketService {
  private socket: Socket | null = null;
  private readonly namespace = '/monitoreo';
  private static skipReasonLogged = false;
  private static proxyTargetMismatchWarned = false;

  /** Acepta `true`, `"true"`, `{ data: true }`, etc. */
  private static esPayloadTicketRegistradoTrue(payload: unknown): boolean {
    if (payload === true) {
      return true;
    }
    if (payload === 'true' || payload === 1) {
      return true;
    }
    if (typeof payload === 'object' && payload !== null && !Array.isArray(payload)) {
      const o = payload as Record<string, unknown>;
      if (
        o['data'] === true ||
        o['ticketRegistrado'] === true ||
        o['movimientoRegistrado'] === true ||
        o['registrado'] === true
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * `movimiento-registrado` suele traer el objeto del movimiento (no solo `true`).
   * Se ignora solo si el payload indica explícitamente fallo.
   */
  private static esDisparadorMovimientoRegistrado(payload: unknown): boolean {
    if (payload === false || payload === 'false' || payload === 0) {
      return false;
    }
    if (payload == null || payload === true || payload === 'true' || payload === 1) {
      return true;
    }
    if (typeof payload === 'string') {
      return payload.trim().length > 0;
    }
    if (typeof payload === 'object' && !Array.isArray(payload)) {
      const o = payload as Record<string, unknown>;
      if (o['ok'] === false || o['success'] === false || o['data'] === false) {
        return false;
      }
      return true;
    }
    return false;
  }

  private readonly connectedSubject = new BehaviorSubject<boolean>(false);
  readonly connected$: Observable<boolean> = this.connectedSubject.asObservable();

  private readonly connectedPayloadSubject = new BehaviorSubject<MonitoreoConnectedPayload | null>(null);
  readonly connectedPayload$: Observable<MonitoreoConnectedPayload | null> = this.connectedPayloadSubject.asObservable();

  /** Emite cuando el servidor envía `ticket-registrado` con payload interpretado como `true`. */
  private readonly ticketRegistradoSubject = new Subject<void>();
  readonly ticketRegistrado$: Observable<void> = this.ticketRegistradoSubject.asObservable();

  /** Igual criterio de payload que `ticket-registrado`; para refrescar reporte de cierre (movimientos). */
  private readonly movimientoRegistradoSubject = new Subject<void>();
  readonly movimientoRegistrado$: Observable<void> = this.movimientoRegistradoSubject.asObservable();

  private readonly entradaProductoStockSubject = new Subject<ProductoStockSocketPayload>();
  readonly entradaProductoStock$: Observable<ProductoStockSocketPayload> =
    this.entradaProductoStockSubject.asObservable();

  private readonly salidaProductoStockSubject = new Subject<ProductoStockSocketPayload>();
  readonly salidaProductoStock$: Observable<ProductoStockSocketPayload> =
    this.salidaProductoStockSubject.asObservable();

  constructor(
    private auth: AuthenticationService,
    private productoService: ProductoService,
    private ngZone: NgZone
  ) {}

  private emitTicketRegistrado(): void {
    this.ngZone.run(() => this.ticketRegistradoSubject.next());
  }

  private emitMovimientoRegistrado(): void {
    this.ngZone.run(() => this.movimientoRegistradoSubject.next());
  }

  private normalizarProductoStockPayload(payload: unknown): ProductoStockSocketPayload | null {
    if (payload == null || typeof payload !== 'object' || Array.isArray(payload)) {
      return null;
    }
    const o = payload as Record<string, unknown>;
    const inner =
      o['data'] != null && typeof o['data'] === 'object' && !Array.isArray(o['data'])
        ? (o['data'] as Record<string, unknown>)
        : o;
    const idProducto = Number(inner['idProducto']);
    const idSucursal = Number(inner['idSucursal']);
    const nuevoStock = Number(inner['nuevoStock']);
    const idSalida = Number(inner['idSalida'] ?? inner['idEntrada']);
    if (
      !Number.isFinite(idProducto) ||
      idProducto <= 0 ||
      !Number.isFinite(idSucursal) ||
      idSucursal <= 0 ||
      !Number.isFinite(nuevoStock) ||
      !Number.isFinite(idSalida) ||
      idSalida <= 0
    ) {
      return null;
    }
    return { idProducto, idSucursal, nuevoStock, idSalida };
  }

  private emitEntradaProductoStock(payload: ProductoStockSocketPayload): void {
    this.ngZone.run(() => this.entradaProductoStockSubject.next(payload));
  }

  private emitSalidaProductoStock(payload: ProductoStockSocketPayload): void {
    this.ngZone.run(() => this.salidaProductoStockSubject.next(payload));
  }

  private getBackendOriginFromApi(): string {
    const env = environment as { API_SECURITY?: string };
    if (!env.API_SECURITY) return '';
    return env.API_SECURITY.replace(/\/api\/?$/, '') || env.API_SECURITY.replace(/\/$/, '');
  }


  private getBaseUrl(): string {
    const env = environment as {
      wsUrl?: string;
      apiUrl?: string;
      API_SECURITY?: string;
      monitoreoSocketUseDevProxy?: boolean;
      production?: boolean;
    };
    if (env.wsUrl) {
      return env.wsUrl.replace(/\/$/, '');
    }
    if (env.apiUrl) {
      return env.apiUrl.replace(/\/$/, '');
    }
    if (!env.production && env.monitoreoSocketUseDevProxy === true && typeof window !== 'undefined') {
      return window.location.origin;
    }
    return this.getBackendOriginFromApi();
  }

  private getSocketIoPath(): string {
    const env = environment as { socketIoPath?: string };
    const p = env.socketIoPath?.trim();
    if (p) {
      return p.startsWith('/') ? p : `/${p}`;
    }
    return '/socket.io';
  }

  private shouldConnectSocket(): boolean {
    const env = environment as { monitoreoSocketEnabled?: boolean };
    return env.monitoreoSocketEnabled !== false;
  }

  
  conectar(): void {
    const token = this.getToken();
    if (!token || token === '') {
      this.connectedSubject.next(false);
      return;
    }

    if (!this.shouldConnectSocket()) {
      this.connectedSubject.next(false);
      if (!(environment as { production?: boolean }).production && !MonitoreoSocketService.skipReasonLogged) {
        MonitoreoSocketService.skipReasonLogged = true;
        console.info('[MonitoreoSocketService] Sin conexión: `monitoreoSocketEnabled: false` en environment.');
      }
      return;
    }

    const baseUrl = this.getBaseUrl();
    if (!baseUrl) {
      console.warn('[MonitoreoSocketService] No hay URL base para WebSocket (wsUrl/apiUrl).');
      return;
    }

 
    if (this.socket) {
      try {
        this.socket.io.reconnection(false);
      } catch {
      }
      try {
        this.socket.io.engine?.close();
      } catch {
      }
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    const path = this.getSocketIoPath();
    const namespaceUrl = `${baseUrl}${this.namespace}`;
    const pathNorm = path.startsWith('/') ? path : `/${path}`;
    const urlPollingEjemplo = `${baseUrl}${pathNorm}/?EIO=4&transport=polling`;

    const envFlags = environment as { monitoreoSocketUseDevProxy?: boolean; production?: boolean };
    const usandoProxyDev =
      !envFlags.production &&
      envFlags.monitoreoSocketUseDevProxy === true &&
      typeof window !== 'undefined' &&
      baseUrl === window.location.origin;
    const backendDirecto = this.getBackendOriginFromApi();

    if (
      !envFlags.production &&
      envFlags.monitoreoSocketUseDevProxy === true &&
      backendDirecto &&
      /localhost|127\.0\.0\.1/i.test(backendDirecto) &&
      !MonitoreoSocketService.proxyTargetMismatchWarned
    ) {
      MonitoreoSocketService.proxyTargetMismatchWarned = true;
      console.warn(
        '[MonitoreoSocketService] Incoherencia: API_SECURITY es localhost pero el socket sale por proxy de ng serve ' +
          '(proxy.conf.js → kashpos). El login es contra :3000 y el socket contra otro servidor → desconexiones / 400. ' +
          'Pon `monitoreoSocketUseDevProxy: false` o apunta API_SECURITY a kashpos.'
      );
    }

   
    const transports: ('polling' | 'websocket')[] = usandoProxyDev ? ['polling'] : ['polling', 'websocket'];

    console.log('[MonitoreoSocketService] ► Socket.IO — copiar para app móvil (v4)', {
      namespaceUrl,
      engineIoPath: pathNorm,
      urlPollingEjemplo,
      transportsUsadosEnEsteCliente: transports,
      transportsRecomendadoMovilProd: ['polling', 'websocket'],
      authEnHandshake: { token: '<JWT igual que en API>' },
      ...(usandoProxyDev && backendDirecto
        ? {
            nota_adminLocalhost:
              'Este namespaceUrl es vía proxy de ng serve (sin CORS). La app móvil debe usar backendDirecto_*; el servidor debe permitir CORS para el origen de la app.',
            backendDirecto_namespaceUrl: `${backendDirecto}${this.namespace}`,
            backendDirecto_urlPollingEjemplo: `${backendDirecto}${pathNorm}/?EIO=4&transport=polling`
          }
        : {})
    });

    this.socket = io(namespaceUrl, {
      path,
      forceNew: true,
      auth: { token },
      transports,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 2000
    });

    this.socket.on('connect', () => {
      this.connectedSubject.next(true);
      console.log('[MonitoreoSocketService] connect (socket.id):', this.socket?.id);
    });

    this.socket.onAny((eventName, ...args) => {
      console.log('[MonitoreoSocketService] evento recibido:', eventName, args);
    });

    this.socket.on('connected', (payload: MonitoreoConnectedPayload) => {
      console.log('[MonitoreoSocketService] socket connected:', payload);
      this.connectedPayloadSubject.next(payload);
    });

    const handleFindProducts = (payload: FindProductsByIdsPayload | Record<string, unknown> | unknown) => {
      console.log('[MonitoreoSocketService] find-products (payload):', payload);
      const p = payload as Record<string, unknown> | null | undefined;
      let productos: unknown[] = [];
      if (p != null && typeof p === 'object' && !Array.isArray(p)) {
        if (Array.isArray(p['productos'])) {
          productos = p['productos'] as unknown[];
        } else if (Array.isArray(p['data'])) {
          productos = p['data'] as unknown[];
        } else if (Array.isArray(p['rows'])) {
          productos = p['rows'] as unknown[];
        } else if (Array.isArray(p['items'])) {
          productos = p['items'] as unknown[];
        } else if (Array.isArray(p['lista'])) {
          productos = p['lista'] as unknown[];
        }
      }
      if (productos.length === 0 && Array.isArray(payload)) {
        productos = payload as unknown[];
      }
      this.ngZone.run(() => this.productoService.notifyProductsUpdated(productos as any[]));
    };

    this.socket.on('find-products-by-ids', handleFindProducts);
    this.socket.on('find-products-by-id', handleFindProducts);

    this.socket.on('ticket-registrado', (payload?: unknown) => {
      if (MonitoreoSocketService.esPayloadTicketRegistradoTrue(payload)) {
        this.emitTicketRegistrado();
      }
    });

    this.socket.on('movimiento-registrado', (payload?: unknown) => {
      if (MonitoreoSocketService.esDisparadorMovimientoRegistrado(payload)) {
        this.emitMovimientoRegistrado();
      }
    });

    this.socket.on('entrada-producto-stock', (payload?: unknown) => {
      const p = this.normalizarProductoStockPayload(payload);
      if (p) {
        this.emitEntradaProductoStock(p);
      }
    });

    this.socket.on('salida-producto-stock', (payload?: unknown) => {
      const p = this.normalizarProductoStockPayload(payload);
      if (p) {
        this.emitSalidaProductoStock(p);
      }
    });

    this.socket.on('connect_error', (err: Error) => {
      this.connectedSubject.next(false);
      this.connectedPayloadSubject.next(null);
      const msg = err?.message ?? String(err);
      console.warn('[MonitoreoSocketService] connect_error:', msg);
      if (!(environment as { production?: boolean }).production) {
        console.info(
          '[MonitoreoSocketService] Ayuda: 404 en `localhost:4200/socket.io` con proxy de dev → reinicia con `npm start` ' +
            '(debe usar `proxy.conf.js`). Si la URL es kashpos y sigue 404, revisa nginx/path del Engine.IO y `socketIoPath`. ' +
            'CORS sin 404: origen http://localhost:4200 permitido en Socket.IO del back.'
        );
      }
    });

    this.socket.on('disconnect', (reason: string) => {
      try {
        this.socket?.io?.reconnection(false);
      } catch {
      }
      this.connectedSubject.next(false);
      this.connectedPayloadSubject.next(null);
      if (reason && reason !== 'io client disconnect') {
        console.warn('[MonitoreoSocketService] disconnect:', reason);
        if (!(environment as { production?: boolean }).production && reason === 'io server disconnect') {
          console.info(
            '[MonitoreoSocketService] El servidor cerró el socket. Si ves GET polling 400 después: ' +
              'suele ser petición en vuelo o backend con varios workers/nginx sin sticky (misma sesión debe ir al mismo Node).'
          );
        }
      }
    });
  }


  desconectar(): void {
    if (this.socket) {
      try {
        this.socket.io.reconnection(false);
      } catch {
      }
      try {
        this.socket.io.engine?.close();
      } catch {
      }
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    MonitoreoSocketService.skipReasonLogged = false;
    this.connectedSubject.next(false);
    this.connectedPayloadSubject.next(null);
  }

  get estaConectado(): boolean {
    return this.connectedSubject.value;
  }

  get ultimoConnectedPayload(): MonitoreoConnectedPayload | null {
    return this.connectedPayloadSubject.value;
  }

  private getToken(): string {
    if (this.auth?.getToken) {
      const t = this.auth.getToken();
      if (t && t !== '') return typeof t === 'string' ? t : (t as unknown as string);
    }
    try {
      const stored = sessionStorage.getItem('token');
      if (stored) {
        const parsed = JSON.parse(stored);
        return typeof parsed === 'string' ? parsed : (parsed?.token ?? parsed ?? '');
      }
    } catch {
    }
    return '';
  }

 
  getSocket(): Socket | null {
    return this.socket;
  }
}
